import { apiRequest } from '@/integrations/api/client';

export interface MesWorkOrder {
  id: string;
  code: string;
  product: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD';
  progress: number;
  planned_qty: number;
  produced_qty: number;
  defect_qty: number;
  line: string | null;
  start_time: string | null;
  created_at: string;
  updated_at: string;
}

export interface MesMachine {
  id: string;
  name: string;
  status: 'RUNNING' | 'PAUSED' | 'ERROR' | 'IDLE' | 'MAINTENANCE';
  current_operator: string | null;
  oee: number;
  line: string | null;
  created_at: string;
  updated_at: string;
}

export interface MesAlert {
  id: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  message: string;
  source: string | null;
  acknowledged: boolean;
  timestamp: string;
  created_at: string;
}

export interface MesEvent {
  id: string;
  type: string;
  station: string | null;
  user: string | null;
  user_name: string | null;
  status: 'OK' | 'WARNING' | 'ERROR' | 'INFO';
  detail: string | null;
  timestamp: string;
  created_at: string;
}

export async function fetchWorkOrders(tenantId?: string | null) {
  const res = await apiRequest<{ work_orders: MesWorkOrder[] }>('/api/mes/work-orders', { tenantId });
  return res.work_orders;
}

export async function fetchMachines(tenantId?: string | null) {
  const res = await apiRequest<{ machines: MesMachine[] }>('/api/mes/machines', { tenantId });
  return res.machines;
}

export async function updateMachineStatus(
  id: string,
  status: MesMachine['status'],
  tenantId?: string | null,
) {
  const res = await apiRequest<{ machine: MesMachine }>(`/api/mes/machines/${id}`, {
    method: 'PATCH',
    tenantId,
    body: { status },
  });
  return res.machine;
}

export async function fetchAlerts(tenantId?: string | null) {
  const res = await apiRequest<{ alerts: MesAlert[] }>('/api/mes/alerts', { tenantId });
  return res.alerts;
}

export async function createAlert(
  input: { severity: MesAlert['severity']; message: string; source?: string; acknowledged?: boolean },
  tenantId?: string | null,
) {
  const res = await apiRequest<{ alert: MesAlert }>('/api/mes/alerts', {
    method: 'POST',
    tenantId,
    body: input,
  });
  return res.alert;
}

export async function acknowledgeAlert(id: string, tenantId?: string | null) {
  const res = await apiRequest<{ alert: MesAlert }>(`/api/mes/alerts/${id}/acknowledge`, {
    method: 'POST',
    tenantId,
  });
  return res.alert;
}

export async function fetchEvents(tenantId?: string | null) {
  const res = await apiRequest<{ events: MesEvent[] }>('/api/mes/events', { tenantId });
  return res.events;
}

export async function createEvent(
  input: {
    type: string;
    station?: string;
    user_name?: string;
    status?: MesEvent['status'];
    detail?: string;
  },
  tenantId?: string | null,
) {
  const res = await apiRequest<{ event: MesEvent }>('/api/mes/events', {
    method: 'POST',
    tenantId,
    body: input,
  });
  return res.event;
}

export async function seedMesDemo(tenantId?: string | null) {
  return apiRequest<{ seeded: boolean }>('/api/mes/seed', { method: 'POST', tenantId });
}
